<script setup lang="ts">
import {useDialogPluginComponent} from 'quasar'
import {useLoginInfoStore} from "@/stores/loginInfo.ts";
import Translator from "@/utils/common/Translator.ts";
import DialogButtons from "@/utils/forms/DialogButtons.ts";
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import {ref} from "vue";
import QuickCreate from "@/components/EditView/QuickCreate.vue";
import ReferenceFieldWidgetUtil from "@/utils/editView/widgets/ReferenceFieldWidgetUtil.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import FieldWidgetUtil from "@/utils/editView/widgets/FieldWidgetUtil.ts";

let props = defineProps<{ newObject: Record<string, any>, fieldUtil?: ReferenceFieldWidgetUtil }>();
const {compactMode} = storeToRefs(useNamaFlagsStore());
const quickCreateRef = ref();

const title: string = Translator.translateId('add') + ' ' + Translator.translateId(props.newObject.entityType);

const {dialogRef, onDialogCancel} = useDialogPluginComponent()
const loginStore = useLoginInfoStore();
const closeDialog = onDialogCancel;

async function onCancelClick() {
  if (!quickCreateRef.value)
    closeDialog();
  else if (await quickCreateRef.value.leaveQuickCreate())
    closeDialog();
}

async function onOkClick() {
  let shouldClose = true;
  if (quickCreateRef.value) {
    await FieldWidgetUtil.saveEditingFieldsAndGrids();
    shouldClose = await quickCreateRef.value.saveRecordAndSelectedIfNeeded();
  }
  if (shouldClose)
    closeDialog();
}

</script>

<template>
  <q-dialog persistent ref="dialogRef" @hide="onCancelClick" v-if="loginStore.isLoggedIn" full-height full-width @keydown.esc="onCancelClick">
    <q-card class="tw-flex tw-flex-col tw-w-full tw-h-full tw-shadow-none tw-bg-namaBackground-bright-light dark:tw-text-white dark:tw-bg-namaBackground-bright-dark">
      <q-bar
        class="tw-flex-none tw-h-fit tw-min-h-8 tw-text-namaFontColor-light tw-bg-[#CED4DA] tw-px-2 tw-py-[6px] tw-flex tw-justify-between dark:tw-bg-[#212529] dark:tw-text-white">
        <div>{{ title }}</div>
        <q-btn dense flat @click="onCancelClick" :title="Translator.translateId( 'close')" class="tw-h-fit">
          <NamaIcon default-icon="nm-win-close"/>
        </q-btn>
      </q-bar>
      <q-card-section class="tw-grow tw-flex tw-flex-col compact:tw-p-0 ultra:tw-p-0">
        <div class="tw-grow tw-bg-namaBackground-bright-light tw-rounded dark:tw-bg-namaBackground-bright-dark">
          <div class="tw-h-full tw-flex tw-flex-col">
            <QuickCreate :fieldUtil="fieldUtil" ref="quickCreateRef" :newObject="newObject"/>
          </div>
        </div>
      </q-card-section>
      <q-card-section class="tw-flex-none tw-h-fit tw-p-0 tw-pb-4 compact:tw-pb-2 ultra:tw-pb-1 tw-flex tw-flex-row tw-gap-4 tw-justify-center">
        <q-btn
          :label="Translator.translateId(DialogButtons.Ok)"
          @click="onOkClick"
          @keydown.enter="onOkClick"
          :dense="compactMode"
          class="tw-h-10 tw-bg-primary-light dark:tw-bg-primary-dark tw-text-white tw-text-base tw-font-normal tw-rounded-md tw-min-w-28 tw-max-w-fit"/>
        <q-btn
          outline
          :label="Translator.translateId(DialogButtons.Cancel)"
          @click="onCancelClick"
          :dense="compactMode"
          class="tw-h-10 tw-text-namaGray dark:tw-text-white tw-text-base tw-font-normal tw-rounded-md tw-min-w-28 tw-max-w-fit"/>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>
